/**
 * src/views/LoginView.tsx
 * Ultra-premium iOS 18 Apple ID / Apple Wallet Authentication Experience
 */

import React, { useState, useEffect } from 'react';
import { ShieldCheck, ArrowRight, Download, Share, PlusSquare, X, User, Lock, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useTheme } from '../context/ThemeContext';
import { haptic } from '../utils/audioHaptics';

export const LoginView: React.FC = () => {
  const { login, isLoading } = useAuth();
  const { success, error } = useToast();
  const { theme, toggleTheme, activeLogo } = useTheme();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // PWA Install state
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isStandalone, setIsStandalone] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
  });
  const [showInstallBanner, setShowInstallBanner] = useState<boolean>(true);
  const [showIOSInstructions, setShowIOSInstructions] = useState<boolean>(false);

  useEffect(() => {
    // Check standalone mode
    const checkStandalone = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
      setIsStandalone(standalone);
    };

    checkStandalone();

    // Listen for beforeinstallprompt event (Android, Chrome, Edge)
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsStandalone(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Listen for appinstalled
    const handleAppInstalled = () => {
      setIsStandalone(true);
      setShowInstallBanner(false);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    haptic.tap();
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === 'accepted') {
        setIsStandalone(true);
        setShowInstallBanner(false);
      }
      setDeferredPrompt(null);
    } else {
      // Toggle Apple iOS instruction helper
      setShowIOSInstructions(prev => !prev);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    haptic.button();
    setErrorMessage('');

    if (!username.trim() || !password) {
      setErrorMessage('Please enter both username and password.');
      haptic.error();
      return;
    }

    const res = await login(username.trim(), password);
    if (res.success) {
      haptic.success();
      success(`Welcome back, ${username}!`, 'CK ID Verified');
    } else {
      haptic.error();
      setErrorMessage(res.error || 'Login failed. Invalid username or password.');
      error(res.error || 'Invalid credentials');
    }
  };

  return (
    <div
      className="ambient-background"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
        minHeight: '100vh',
        position: 'relative'
      }}
    >
      {/* Top iOS Status Controls */}
      <div style={{ position: 'absolute', top: '24px', right: '24px' }}>
        <button
          onClick={() => {
            haptic.tap();
            toggleTheme();
          }}
          style={{
            height: '38px',
            padding: '0 14px',
            borderRadius: 'var(--radius-pill)',
            border: '1px solid var(--border-highlight)',
            background: 'var(--ios-card-bg)',
            color: 'var(--text-primary)',
            fontSize: '0.82rem',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            backdropFilter: 'blur(30px)',
            transition: 'all 0.2s cubic-bezier(0.2, 0.9, 0.3, 1)'
          }}
          className="ios-tap-target"
          title="Toggle Theme"
        >
          <span>{theme === 'dark' ? '☀️' : '🌙'}</span>
          <span style={{ fontSize: '0.76rem', color: 'var(--text-secondary)' }}>
            {theme === 'dark' ? 'Light' : 'Dark'}
          </span>
        </button>
      </div>

      <div style={{ width: '100%', maxWidth: '420px' }}>
        {/* Apple Squircle Brand Icon & Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div
            style={{
              width: '84px',
              height: '84px',
              borderRadius: '24px',
              overflow: 'hidden',
              boxShadow: theme === 'dark' 
                ? '0 16px 40px rgba(10, 132, 255, 0.35), inset 0 1px 1px rgba(255, 255, 255, 0.2)' 
                : '0 16px 40px rgba(0, 0, 0, 0.12), inset 0 1px 1px rgba(255, 255, 255, 0.8)',
              border: '1px solid var(--border-highlight)',
              margin: '0 auto 18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--surface-primary)',
              position: 'relative'
            }}
          >
            <img
              src={activeLogo}
              alt="CKAudit Logo"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </div>

          <h1
            style={{
              fontSize: '1.9rem',
              fontWeight: 800,
              letterSpacing: '-0.04em',
              marginBottom: '6px',
              color: 'var(--text-primary)'
            }}
          >
            CKAudit
          </h1>
          <p
            style={{
              color: 'var(--text-secondary)',
              fontSize: '0.88rem',
              fontWeight: 500,
              letterSpacing: '-0.01em'
            }}
          >
            Sign in with your CK ID
          </p>
        </div>

        {/* Apple Card Inset Authentication Container */}
        <div
          className="apple-card"
          style={{
            padding: '28px 24px',
            borderRadius: '28px',
            border: '1px solid var(--ios-glass-border)',
            background: 'var(--ios-card-bg)',
            backdropFilter: 'blur(40px) saturate(190%)',
            boxShadow: '0 24px 48px rgba(0, 0, 0, 0.35)'
          }}
        >
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {errorMessage && (
              <div
                style={{
                  padding: '12px 16px',
                  borderRadius: '14px',
                  background: 'rgba(255, 69, 58, 0.12)',
                  border: '1px solid rgba(255, 69, 58, 0.3)',
                  color: 'var(--ios-red)',
                  fontSize: '0.84rem',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span>⚠️</span>
                <span>{errorMessage}</span>
              </div>
            )}

            {/* Inset Group for Inputs (Apple Settings style) */}
            <div
              className="ios-inset-group"
              style={{
                margin: 0,
                background: theme === 'dark' ? 'rgba(24, 34, 52, 0.65)' : 'rgba(255, 255, 255, 0.85)',
                border: '1px solid var(--border-highlight)',
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: theme === 'dark'
                  ? 'inset 0 1px 1px rgba(255, 255, 255, 0.08), 0 4px 16px rgba(0, 0, 0, 0.25)'
                  : 'inset 0 1px 1px rgba(255, 255, 255, 0.9), 0 4px 16px rgba(0, 0, 0, 0.06)'
              }}
            >
              {/* Username Field */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  padding: '14px 18px',
                  borderBottom: '0.5px solid var(--border-subtle)'
                }}
              >
                <div
                  className="ios-icon-squircle blue"
                  style={{ width: '36px', height: '36px', borderRadius: '10px', flexShrink: 0 }}
                >
                  <User size={18} strokeWidth={2.4} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '0.68rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      fontWeight: 800,
                      color: 'var(--text-tertiary)',
                      marginBottom: '3px'
                    }}
                  >
                    Username
                  </label>
                  <input
                    type="text"
                    placeholder="Enter your username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="username"
                    required
                    className="ios-login-input"
                    style={{
                      width: '100%',
                      background: 'transparent',
                      border: 'none',
                      outline: 'none',
                      color: 'var(--text-primary)',
                      fontSize: '1rem',
                      fontWeight: 600,
                      fontFamily: 'inherit',
                      padding: 0
                    }}
                  />
                </div>
              </div>

              {/* Password Field */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  padding: '14px 18px'
                }}
              >
                <div
                  className="ios-icon-squircle indigo"
                  style={{ width: '36px', height: '36px', borderRadius: '10px', flexShrink: 0 }}
                >
                  <Lock size={18} strokeWidth={2.4} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: '0.68rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      fontWeight: 800,
                      color: 'var(--text-tertiary)',
                      marginBottom: '3px'
                    }}
                  >
                    Password
                  </label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                    className="ios-login-input"
                    style={{
                      width: '100%',
                      background: 'transparent',
                      border: 'none',
                      outline: 'none',
                      color: 'var(--text-primary)',
                      fontSize: '1.05rem',
                      letterSpacing: showPassword ? 'normal' : '0.12em',
                      fontFamily: 'inherit',
                      padding: 0
                    }}
                  />
                </div>

                {/* Show/Hide Password Eye Toggle */}
                <button
                  type="button"
                  onClick={() => {
                    haptic.tap();
                    setShowPassword(prev => !prev);
                  }}
                  className="ios-tap-target"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-tertiary)',
                    cursor: 'pointer',
                    padding: '6px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                  title={showPassword ? 'Hide password' : 'Show password'}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Apple Pay / iOS Primary Pill Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="ios-primary-button ios-tap-target"
              style={{
                width: '100%',
                padding: '16px',
                fontSize: '0.98rem',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              {isLoading ? (
                <>
                  <Loader2 size={18} className="spin" />
                  <span>Authenticating...</span>
                </>
              ) : (
                <>
                  <span>Sign In with CK ID</span>
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          {/* Scoped Autofill Protection Styles */}
          <style>{`
            .ios-login-input:-webkit-autofill,
            .ios-login-input:-webkit-autofill:hover,
            .ios-login-input:-webkit-autofill:focus,
            .ios-login-input:-webkit-autofill:active {
              -webkit-box-shadow: 0 0 0 1000px ${theme === 'dark' ? '#182234' : '#f8fafc'} inset !important;
              -webkit-text-fill-color: ${theme === 'dark' ? '#ffffff' : '#0f172a'} !important;
              caret-color: ${theme === 'dark' ? '#ffffff' : '#0f172a'} !important;
              transition: background-color 5000s ease-in-out 0s !important;
              font-family: inherit !important;
            }
          `}</style>
        </div>

        {/* PWA Install Invitation Banner (Visible when app is opened in browser and not installed) */}
        {!isStandalone && showInstallBanner && (
          <div
            style={{
              marginTop: '18px',
              borderRadius: '22px',
              padding: '16px 18px',
              background: theme === 'dark' ? 'rgba(16, 24, 39, 0.82)' : 'rgba(255, 255, 255, 0.88)',
              border: '1px solid var(--border-highlight)',
              backdropFilter: 'blur(30px)',
              boxShadow: '0 12px 32px rgba(0, 0, 0, 0.22)',
              position: 'relative',
              animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <div
                style={{
                  width: '46px',
                  height: '46px',
                  borderRadius: '13px',
                  overflow: 'hidden',
                  flexShrink: 0,
                  boxShadow: '0 4px 14px rgba(10, 132, 255, 0.35)',
                  border: '1px solid rgba(255, 255, 255, 0.18)',
                  background: 'var(--surface-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <img
                  src={activeLogo}
                  alt="CKAudit App"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '0.88rem', fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
                    Install CKAudit App
                  </span>
                  <span style={{
                    fontSize: '0.625rem',
                    padding: '2px 6px',
                    borderRadius: '8px',
                    background: 'rgba(10, 132, 255, 0.16)',
                    color: 'var(--ios-blue)',
                    fontWeight: 800,
                    letterSpacing: '0.04em'
                  }}>
                    PWA
                  </span>
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: '2px', lineHeight: 1.35 }}>
                  Add to Home Screen for native offline speed and full-screen view.
                </div>
              </div>

              <button
                type="button"
                onClick={handleInstallClick}
                style={{
                  padding: '8px 14px',
                  borderRadius: 'var(--radius-pill)',
                  background: 'var(--ios-blue)',
                  color: '#ffffff',
                  border: 'none',
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 4px 12px rgba(10, 132, 255, 0.4)'
                }}
                className="ios-tap-target"
              >
                <Download size={13} strokeWidth={2.8} />
                <span>Install</span>
              </button>

              <button
                type="button"
                onClick={() => setShowInstallBanner(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-tertiary)',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Dismiss"
              >
                <X size={15} />
              </button>
            </div>

            {/* Apple iOS Safari 2-step Add to Home Screen Instructions */}
            {showIOSInstructions && (
              <div
                style={{
                  marginTop: '12px',
                  padding: '12px 14px',
                  borderRadius: '14px',
                  background: 'rgba(10, 132, 255, 0.08)',
                  border: '1px dashed rgba(10, 132, 255, 0.35)',
                  fontSize: '0.76rem',
                  color: 'var(--text-primary)',
                  lineHeight: 1.55,
                  animation: 'fadeIn 0.25s ease-out'
                }}
              >
                <div style={{ fontWeight: 800, marginBottom: '4px', color: 'var(--ios-blue)' }}>
                  iPhone & iPad Safari Installation:
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                  <span>1. Tap the</span>
                  <span style={{ fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                    Share <Share size={13} color="var(--ios-blue)" />
                  </span>
                  <span>button in Safari's toolbar</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                  <span>2. Scroll down & tap</span>
                  <span style={{ fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                    Add to Home Screen <PlusSquare size={13} color="var(--ios-blue)" />
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Security & Architecture Badge */}
        <div
          style={{
            textAlign: 'center',
            marginTop: '28px',
            fontSize: '0.75rem',
            color: 'var(--text-tertiary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px'
          }}
        >
          <span>🔒</span>
          <span>End-to-End Encrypted & Authenticated Session</span>
        </div>
      </div>
    </div>
  );
};

